import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { SearchableSelect, colors, useGraphQLQuery } from '@tviklax/ui-components';

const MY_ACCOUNTS_SEARCH_QUERY = gql`
  query MyAccountsSearch($accountType: String!, $search: String, $limit: Int) {
    myAccounts(accountType: $accountType, search: $search, limit: $limit) {
      items {
        id
        name
        email
        phone
        country
      }
    }
  }
`;

export interface AccountOption {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
}

interface AccountAutocompleteProps {
  accountType: 'vendor' | 'customer';
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAccountSelected: (account: AccountOption) => void;
  placeholder?: string;
  required?: boolean;
}

// Typeahead over your existing trade-partner roster (see the Accounts page)
// — picking a suggestion autofills email/phone/country from that account
// instead of retyping them. Typing something that doesn't match anything
// is still fine: it falls through to the same freeform behavior as before,
// creating a new account at order-creation time. A thin GraphQL-query
// wrapper around the shared SearchableSelect's freeform mode, rather than
// its own bespoke dropdown.
export const AccountAutocomplete: React.FC<AccountAutocompleteProps> = ({
  accountType,
  label,
  name,
  value,
  onChange,
  onAccountSelected,
  placeholder,
  required,
}) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), 250);
    return () => clearTimeout(timer);
  }, [value]);

  const { data } = useGraphQLQuery<{ myAccounts: { items: AccountOption[] } }>(MY_ACCOUNTS_SEARCH_QUERY, {
    showErrorToast: false,
    variables: { accountType, search: debouncedValue || null, limit: 6 },
    skip: debouncedValue.trim().length < 2,
  });

  const suggestions = data?.myAccounts.items ?? [];

  return (
    <SearchableSelect<AccountOption>
      label={label}
      required={required}
      placeholder={placeholder}
      options={suggestions}
      filterLocally={false}
      getOptionKey={(account) => account.id}
      getOptionLabel={(account) => account.name}
      inputValue={value}
      onInputValueChange={(text) => onChange({ target: { name, value: text } } as React.ChangeEvent<HTMLInputElement>)}
      onChange={onAccountSelected}
      emptyMessage="No matches on your roster"
      renderOption={(account) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{account.name}</div>
          <div style={{ fontSize: 12, color: colors.gray500 }}>
            {[account.email, account.country].filter(Boolean).join(' · ') || 'No details on file'}
          </div>
        </div>
      )}
    />
  );
};
